import { EColorGradients } from '@/types/color-gradients.types';
import { ThemePack } from '@/types/theme-packs.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Modal from '@ui/modal/modal';
import styles from './theme-pack-delete-modal.module.css';
import { FetchError } from '@/connection/helpers';
import toastTrigger from '@ui/toast/toast-trigger';
import { useAppDispatch } from '@/store/hooks';
import { deleteThemePackSync } from '@/store/slices/templates';
import { DeleteColors } from '@/connection/theme-packs';
import { useEffect, useState } from 'react';
import { EToastTypes } from '@ui/toast/toast.types';

type Props = {
  tempSelectedTheme: ThemePack | null;
  open: boolean;
  close: () => void;
};

export default function ThemePackDeleteModal({ open, close, tempSelectedTheme }: Props) {
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const deleteTheme = async () => {
    try {
      if (!tempSelectedTheme) return;
      setDeleting(true);
      const response = await DeleteColors([tempSelectedTheme.color_pack], tempSelectedTheme.channel);
      if (response.error) {
        throw response.error;
      }

      if (response.cannotDelete.length) {
        setError(`This theme cannot be deleted because it is in use by another project.`);
      } else {
        dispatch(deleteThemePackSync(response.deletedColors));
        close();
      }
    } catch (e) {
      console.error(e);
      if (e instanceof FetchError && e.response.status === 400) {
        toastTrigger(e.body?.error ?? 'Unable to delete theme', {
          type: EToastTypes.error,
        });
        return;
      }
      toastTrigger('Unable to delete theme', {
        type: EToastTypes.error,
      });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setError(null);
  }, [tempSelectedTheme]);

  if (!tempSelectedTheme) return null;
  return (
    <Modal
      open={open}
      customSize={{
        maxHeight: '200px',
      }}
      contentWrapperClassName={styles['modal-body']}
      modalClassName={styles.modal}
      RenderHeaderContent={() => (
        <Container padding="20px 40px 20px 40px" background="transparent">
          <h2>Delete Theme Pack</h2>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container padding="0 40px 20px 40px" className="flex jc-flex-end gap-8 height-64" background="transparent">
          <div className="flex gap-8">
            <Button
              onClick={close}
              backgroundColor="var(--gray-650)"
              hoverBackgroundColor="var(--gray-600)"
              className="L1"
            >
              Cancel
            </Button>
            <Button
              onClick={deleteTheme}
              gradientProps={{
                color: EColorGradients.Sunrise,
              }}
              disabledOpacity={'0.5'}
              className={'flex ai-center jc-center'}
              disabled={deleting || !!error}
              loading={deleting}
            >
              <Text color="var(--gray-true-dark-1000)" className="L1" whiteSpace="nowrap">
                Delete
              </Text>
            </Button>
          </div>
        </Container>
      )}
    >
      <Container background="transparent" className="flex flex-column gap-36 jc-center" padding="0 40px">
        <Text className={error ? 'error' : ''}>
          {error ? (
            error
          ) : (
            <>
              Are you sure you want to delete{' '}
              <strong>
                <em>{tempSelectedTheme?.title}</em>
              </strong>
              ?
            </>
          )}
        </Text>
      </Container>
    </Modal>
  );
}
